import React, { FC, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import { analyticsConfigQuery, orScheduleMetricsQuery } from '../../../graph/dashboard';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Popover from '@material-ui/core/Popover';
import GanttChart, {
  ORScheduleMetrics,
  TooltipData,
  timeToMinutes,
  Procedure,
  createPhysicianColorMap,
} from './GanttChart';
import { alpha, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';

interface ORScheduleAnalyticsProps {
  filter: any;
  setFilter: any;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    height: '100%',
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  metricsCard: {
    padding: theme.spacing(2),
  },
  metricBox: {
    padding: theme.spacing(1, 2),
    borderLeft: `4px solid ${theme.palette.divider}`,
    height: '100%',
  },
  metricTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 500,
  },
  metricLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  chartContainer: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  chartToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  chartTitleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  timeControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  timeSelect: {
    minWidth: 80,
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
    },
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  procedureCount: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  legendSwatch: {
    display: 'inline-block',
    width: 16,
    height: 8,
    borderRadius: 2,
    marginRight: 6,
  },
  legendSwatchActual: {
    background: '#3b82f6',
  },
  legendSwatchScheduled: {
    background: 'rgba(59,130,246,0.6)',
    height: 4,
  },
  legendDash: {
    display: 'inline-block',
    width: 16,
    height: 0,
    borderTop: '2px dashed #059669',
    marginRight: 6,
    position: 'relative',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  emptyStateTitle: {
    opacity: 0.5,
  },
  fcotsOnTime: {
    color: '#10b981',
  },
  fcotsDelayed: {
    color: '#ef4444',
  },
  fcotsGracePeriod: {
    display: 'block',
    marginTop: theme.spacing(1),
  },
  lpodOnTime: {
    color: '#10b981',
  },
  lpodDelayed: {
    color: '#ef4444',
  },
  symbolsChip: {
    cursor: 'pointer',
  },
  symbolsPopover: {
    padding: theme.spacing(2),
    minWidth: 240,
  },
  symbolRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
  },
  symbolIcon: {
    width: 24,
    height: 16,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  symbolLabel: {
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
  },
  symbolsTitle: {
    fontWeight: 700,
    fontSize: '0.8125rem',
    marginBottom: theme.spacing(0.5),
  },
}));

const useTooltipStyles = makeStyles(theme => ({
  tooltipRoot: {
    position: 'fixed',
    zIndex: 9999,
    left: 0,
    top: 0,
    borderRadius: 8,
    pointerEvents: 'none',
    opacity: 0,
    willChange: 'transform, opacity',
    maxHeight: 'calc(100vh - 32px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    borderWidth: 2,
    borderColor: 'transparent',
    borderStyle: 'solid',
  },
  tooltipHeader: {
    padding: theme.spacing(0.75, 1),
    backgroundColor: alpha(theme.palette.common.white, 0.05),
  },
  physicianIndicator: {
    borderRadius: '100%',
    width: 8,
    height: 8,
    backgroundColor: 'white',
  },
  physicianName: {
    fontWeight: 'bold',
    lineHeight: 1.2,
    marginBottom: 0,
  },
  patientName: {
    lineHeight: 1.2,
    marginLeft: theme.spacing(1.5),
  },
  tooltipPatient: {
    fontWeight: 700,
    color: 'white',
    marginBottom: 4,
  },
  tooltipPhysician: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  tooltipSectionTitle: {
    fontWeight: 700,
    color: '#22d3ee',
    marginBottom: 4,
  },
  tooltipSectionTitleSpaced: {
    marginTop: 8,
  },
  tooltipDivider: {
    borderColor: 'rgba(255,255,255,0.3)',
    margin: '8px 0',
  },
  tooltipVariancePositive: {
    color: '#f87171',
  },
  tooltipVarianceNegative: {
    color: '#4ade80',
  },
  tooltipVarianceNeutral: {
    color: '#d1d5db',
  },
}));

const minutesToHHMM = (totalMinutes: number): string => {
  const hour = Math.floor(totalMinutes / 60) % 24;
  const minute = Math.round(totalMinutes % 60);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const TOOLTIP_PADDING = 16;
const TOOLTIP_FALLBACK_WIDTH = 320;
const TOOLTIP_FALLBACK_HEIGHT = 220;

interface ProcedureTooltipProps {
  procedure: Procedure;
  tooltipRef: React.RefObject<HTMLDivElement>;
  physicianColorMap: Map<string, string>;
}

const ProcedureTooltip: FC<ProcedureTooltipProps> = ({ procedure, tooltipRef, physicianColorMap }) => {
  const proc = procedure;
  const physicianId = proc.physician?.id;
  const physicianColor = (physicianId && physicianColorMap.get(physicianId)) || '#6b7280';
  const physicianName = proc.physician?.name || 'Unknown';

  const tooltipClasses = useTooltipStyles();

  const actualStartMin = proc.actualStartTime ? timeToMinutes(proc.actualStartTime) : null;
  const actualEndMin =
    actualStartMin !== null && proc.actualDuration ? actualStartMin + Math.round(proc.actualDuration) : null;
  const scheduledStartMin = proc.scheduledStartTime ? timeToMinutes(proc.scheduledStartTime) : null;
  const scheduledEndMin =
    scheduledStartMin !== null && proc.scheduledDuration
      ? scheduledStartMin + Math.round(proc.scheduledDuration)
      : null;

  const variance = actualStartMin !== null && scheduledStartMin !== null ? actualStartMin - scheduledStartMin : null;

  const empty = '—';
  const actualStartStr = actualStartMin !== null ? minutesToHHMM(actualStartMin) : empty;
  const actualEndStr = actualEndMin !== null ? minutesToHHMM(actualEndMin) : empty;
  const scheduledStartStr = scheduledStartMin !== null ? minutesToHHMM(scheduledStartMin) : empty;
  const scheduledEndStr = scheduledEndMin !== null ? minutesToHHMM(scheduledEndMin) : empty;

  // New time fields
  const orEntryMin = proc.orEntry ? timeToMinutes(proc.orEntry) : null;
  const orExitMin = proc.orExit ? timeToMinutes(proc.orExit) : null;
  const procedureStartMin = proc.procedureStart ? timeToMinutes(proc.procedureStart) : null;
  const closingStartMin = proc.closingStart ? timeToMinutes(proc.closingStart) : null;
  const procedureEndMin = proc.procedureEnd ? timeToMinutes(proc.procedureEnd) : null;
  const anesthesiaStartMin = proc.anesthesiaStart ? timeToMinutes(proc.anesthesiaStart) : null;
  const readyForSurgeryMin = proc.readyForSurgery ? timeToMinutes(proc.readyForSurgery) : null;
  const surgeonLeftORMin = proc.surgeonLeftOR ? timeToMinutes(proc.surgeonLeftOR) : null;
  const physicianInMin = proc.physicianIn ? timeToMinutes(proc.physicianIn) : null;

  const orEntryStr = orEntryMin !== null ? minutesToHHMM(orEntryMin) : empty;
  const orExitStr = orExitMin !== null ? minutesToHHMM(orExitMin) : empty;
  const procedureStartStr = procedureStartMin !== null ? minutesToHHMM(procedureStartMin) : empty;
  const closingStartStr = closingStartMin !== null ? minutesToHHMM(closingStartMin) : empty;
  const procedureEndStr = procedureEndMin !== null ? minutesToHHMM(procedureEndMin) : empty;
  const anesthesiaStartStr = anesthesiaStartMin !== null ? minutesToHHMM(anesthesiaStartMin) : empty;
  const readyForSurgeryStr = readyForSurgeryMin !== null ? minutesToHHMM(readyForSurgeryMin) : empty;
  const surgeonLeftORStr = surgeonLeftORMin !== null ? minutesToHHMM(surgeonLeftORMin) : empty;
  const physicianInStr = physicianInMin !== null ? minutesToHHMM(physicianInMin) : empty;

  const deltaEnd = actualEndMin !== null && scheduledEndMin !== null ? actualEndMin - scheduledEndMin : null;
  const deltaDuration =
    proc.scheduledDuration !== null && proc.actualDuration !== null
      ? proc.actualDuration - proc.scheduledDuration
      : null;

  const StyledTableCell = withStyles(theme => ({
    head: {
      color: theme.palette.text.secondary,
      fontSize: 10,
      textTransform: 'uppercase',
      padding: theme.spacing(0.5, 1),
      textAlign: 'center',
      lineHeight: 1.2,
    },
    body: {
      fontSize: 10,
      padding: theme.spacing(0.1, 1),
      fontWeight: 'bold',
      // textAlign: 'center',
    },
  }))(TableCell);

  const StyledFirstTableCell = withStyles(theme => ({
    root: {
      color: theme.palette.text.secondary,
      fontSize: 10,
      textTransform: 'uppercase',
      padding: theme.spacing(0, 0.5, 0, 0),
      lineHeight: 1,
      width: 110,
    },
  }))(TableCell);

  return (
    <Paper ref={tooltipRef} className={tooltipClasses.tooltipRoot} style={{ borderColor: physicianColor }}>
      <Box className={tooltipClasses.tooltipHeader}>
        <Box className={tooltipClasses.tooltipPhysician}>
          <div className={tooltipClasses.physicianIndicator} style={{ backgroundColor: physicianColor }} />
          <Typography variant="body2" className={tooltipClasses.physicianName}>
            {physicianName}
          </Typography>
        </Box>
        <Typography color="textSecondary" variant="body2" className={tooltipClasses.patientName}>
          {proc.patientName}
        </Typography>
      </Box>

      <Box px={1} pt={1}>
        <Typography variant="caption" style={{ fontWeight: 'bold' }}>
          Scheduled
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <StyledFirstTableCell>Procedure Start</StyledFirstTableCell>
                <StyledTableCell>{scheduledStartStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Procedure End</StyledFirstTableCell>
                <StyledTableCell>{scheduledEndStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Duration</StyledFirstTableCell>
                <StyledTableCell>
                  {proc.scheduledDuration ? `${Math.round(proc.scheduledDuration)} min` : empty}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box p={1}>
        <Typography variant="caption" style={{ fontWeight: 'bold' }}>
          Actual
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <StyledFirstTableCell>Entered OR</StyledFirstTableCell>
                <StyledTableCell>
                  {orEntryStr}{' '}
                  {variance !== null ? (
                    <span
                      className={
                        variance > 0
                          ? tooltipClasses.tooltipVariancePositive
                          : variance < 0
                            ? tooltipClasses.tooltipVarianceNegative
                            : tooltipClasses.tooltipVarianceNeutral
                      }
                    >
                      {variance > 0 ? '+' : variance < 0 ? '–' : ''}
                      {Math.abs(variance)} min
                    </span>
                  ) : (
                    empty
                  )}
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Anesthesia Start</StyledFirstTableCell>
                <StyledTableCell>{anesthesiaStartStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Ready for Surgery</StyledFirstTableCell>
                <StyledTableCell>{readyForSurgeryStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Physician In</StyledFirstTableCell>
                <StyledTableCell>{physicianInStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Procedure Start</StyledFirstTableCell>
                <StyledTableCell>{procedureStartStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Closing Start</StyledFirstTableCell>
                <StyledTableCell>{closingStartStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Procedure End</StyledFirstTableCell>
                <StyledTableCell>{procedureEndStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Surgeon Left OR</StyledFirstTableCell>
                <StyledTableCell>{surgeonLeftORStr}</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>OR Exit</StyledFirstTableCell>
                <StyledTableCell>
                  {orExitStr}{' '}
                  {deltaEnd !== null ? (
                    <span
                      className={
                        deltaEnd > 0
                          ? tooltipClasses.tooltipVariancePositive
                          : deltaEnd < 0
                            ? tooltipClasses.tooltipVarianceNegative
                            : tooltipClasses.tooltipVarianceNeutral
                      }
                    >
                      {deltaEnd > 0 ? '+' : deltaEnd < 0 ? '–' : ''}
                      {Math.abs(deltaEnd)} min
                    </span>
                  ) : (
                    empty
                  )}
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledFirstTableCell>Duration</StyledFirstTableCell>
                <StyledTableCell>
                  {proc.actualDuration ? `${Math.round(proc.actualDuration)} min` : empty}{' '}
                  {deltaDuration !== null ? (
                    <span
                      className={
                        deltaDuration > 0
                          ? tooltipClasses.tooltipVariancePositive
                          : deltaDuration < 0
                            ? tooltipClasses.tooltipVarianceNegative
                            : tooltipClasses.tooltipVarianceNeutral
                      }
                    >
                      {deltaDuration > 0 ? '+' : deltaDuration < 0 ? '–' : ''}
                      {Math.abs(deltaDuration)} min
                    </span>
                  ) : (
                    empty
                  )}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/*<Box className={`${tooltipClasses.tooltipSectionTitle} ${tooltipClasses.tooltipSectionTitleSpaced}`}>*/}
      {/*  Scheduled Segment*/}
      {/*</Box>*/}
      {/*<Box>*/}
      {/*  Start: {scheduledStartStr} | End: {scheduledEndStr}*/}
      {/*</Box>*/}
      {/*<Box>Duration: {proc.scheduledDuration ? `${Math.round(proc.scheduledDuration)} min` : 'N/A'}</Box>*/}
    </Paper>
  );
};

const ORScheduleAnalytics: FC<ORScheduleAnalyticsProps> = ({ filter }) => {
  const classes = useStyles();
  const [chartStartHour, setChartStartHour] = useState(6);
  const [chartEndHour, setChartEndHour] = useState(18);
  const [hoveredProcedure, setHoveredProcedure] = useState<Procedure | null>(null);
  const [symbolsAnchorEl, setSymbolsAnchorEl] = useState<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<{ x: number; y: number } | null>(null);

  const clampTooltipPosition = useCallback((position: TooltipData['position']) => {
    if (typeof window === 'undefined') {
      return position;
    }

    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width || TOOLTIP_FALLBACK_WIDTH;
    const tooltipHeight = tooltipRect?.height || TOOLTIP_FALLBACK_HEIGHT;

    const maxX = Math.max(TOOLTIP_PADDING, window.innerWidth - tooltipWidth - TOOLTIP_PADDING);
    const maxY = Math.max(TOOLTIP_PADDING, window.innerHeight - tooltipHeight - TOOLTIP_PADDING);

    return {
      x: Math.min(Math.max(TOOLTIP_PADDING, position.x), maxX),
      y: Math.min(Math.max(TOOLTIP_PADDING, position.y), maxY),
    };
  }, []);

  // Update tooltip position imperatively (no React re-render)
  const updateTooltipPosition = useCallback(() => {
    const el = tooltipRef.current;
    const pos = positionRef.current;
    if (el && pos) {
      const clampedPosition = clampTooltipPosition(pos);
      el.style.transform = `translate(${clampedPosition.x}px, ${clampedPosition.y}px)`;
      el.style.opacity = '1';
    }
  }, [clampTooltipPosition]);

  const handleProcedureHover = useCallback(
    (data: TooltipData | null) => {
      if (!data) {
        // Hide tooltip
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = '0';
        }
        positionRef.current = null;
        setHoveredProcedure(null);
        return;
      }

      // Store raw pointer position and clamp after the tooltip has been measured
      positionRef.current = data.position;

      // Only trigger React re-render if procedure changed
      setHoveredProcedure(prev => {
        if (prev === data.procedure) {
          // Same procedure, just update position imperatively
          updateTooltipPosition();
          return prev;
        }
        return data.procedure;
      });
    },
    [updateTooltipPosition]
  );

  // When hoveredProcedure changes (new procedure), update position after render
  useEffect(() => {
    if (hoveredProcedure) {
      updateTooltipPosition();
    }
  }, [hoveredProcedure, updateTooltipPosition]);

  const selectedDate = useMemo(() => {
    if (!filter?.selectedDate) return null;
    return filter.selectedDate.toString();
  }, [filter?.selectedDate]);

  const { operationRooms, physicians } = filter || {};
  const sanitizedFilter = useMemo(() => {
    const result: any = {};
    if (Array.isArray(operationRooms) && operationRooms.length > 0) {
      result.operationRooms = operationRooms.map(or => parseInt(or, 10));
    }
    if (Array.isArray(physicians) && physicians.length > 0) {
      result.physicians = physicians.map(py => parseInt(py, 10));
    }
    return result;
  }, [operationRooms, physicians]);

  const analyticsConfig = useQuery(analyticsConfigQuery);
  const fcotsGracePeriod = analyticsConfig?.data?.analyticsConfiguration?.fcotsGracePeriod;

  const { data: metricsData, loading } = useQuery(orScheduleMetricsQuery, {
    variables: {
      date: selectedDate || '',
      filter: sanitizedFilter,
      fcotsGracePeriod,
    },
    skip: !selectedDate,
    fetchPolicy: 'cache-and-network',
  });

  const metrics: ORScheduleMetrics | null = metricsData?.orScheduleMetrics || null;

  // Create physician color map once per metrics change - stable within a render
  const physicianColorMap = useMemo(() => {
    if (!metrics?.orMetrics) return new Map<string, string>();
    return createPhysicianColorMap(metrics.orMetrics);
  }, [metrics?.orMetrics]);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={30} />
        <Typography variant="body1" color="textSecondary">
          Loading OR Schedule Analytics...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Metrics Cards */}
      {metrics && (
        <Paper className={classes.metricsCard}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box className={classes.metricBox}>
                <Typography className={classes.metricTitle} variant="h6">
                  FCOTS (First Case On-Time Start)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>Average Delay:</Typography>
                    <Typography
                      className={`${classes.metricValue} ${
                        metrics.avgFcots !== null && metrics.avgFcots <= 0 ? classes.fcotsOnTime : classes.fcotsDelayed
                      }`}
                    >
                      {metrics.avgFcots !== null
                        ? `${metrics.avgFcots > 0 ? '+' : ''}${Math.round(metrics.avgFcots)} min`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>On-Time Rate:</Typography>
                    <Typography className={classes.metricValue}>
                      {metrics.fcotsOnTimeRate !== null ? `${metrics.fcotsOnTimeRate.toFixed(1)}%` : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
                {fcotsGracePeriod != null && (
                  <Typography variant="caption" color="textSecondary" className={classes.fcotsGracePeriod}>
                    Grace period: {fcotsGracePeriod} min
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box className={classes.metricBox}>
                <Typography className={classes.metricTitle} variant="h6">
                  Utilization
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>Rate:</Typography>
                    <Typography className={classes.metricValue}>
                      {metrics.avgUtilization !== null ? `${metrics.avgUtilization.toFixed(1)}%` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>Duration:</Typography>
                    <Typography className={classes.metricValue}>
                      {metrics.totalSurgeryTimeHours !== null
                        ? `${metrics.totalSurgeryTimeHours.toFixed(1)} hrs`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box className={classes.metricBox}>
                <Typography className={classes.metricTitle} variant="h6">
                  Turnover Time
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>General:</Typography>
                    <Typography className={classes.metricValue}>
                      {metrics.avgGeneralTurnover !== null ? `${Math.round(metrics.avgGeneralTurnover)} min` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.metricLabel}>Same Surgeon:</Typography>
                    <Typography className={classes.metricValue}>
                      {metrics.avgSurgeonTurnover !== null ? `${Math.round(metrics.avgSurgeonTurnover)} min` : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box className={classes.metricBox}>
                <Typography className={classes.metricTitle} variant="h6">
                  LPOD (Last Patient Out Delay)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography className={classes.metricLabel}>Average Delay:</Typography>
                    <Typography
                      className={`${classes.metricValue} ${
                        metrics.lastPatientOutDelay !== null && metrics.lastPatientOutDelay !== undefined
                          ? metrics.lastPatientOutDelay <= 0
                            ? classes.lpodOnTime
                            : classes.lpodDelayed
                          : ''
                      }`}
                    >
                      {metrics.lastPatientOutDelay !== null && metrics.lastPatientOutDelay !== undefined
                        ? `${metrics.lastPatientOutDelay > 0 ? '+' : ''}${Math.round(metrics.lastPatientOutDelay)} min`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Gantt Chart */}
      {metrics && metrics.orMetrics.length > 0 ? (
        <Paper className={classes.chartContainer}>
          <Box className={classes.chartToolbar}>
            <Box className={classes.chartTitleSection}>
              <Typography variant="h6">OR Schedule Timeline</Typography>
              <Typography className={classes.procedureCount}>
                {metrics.totalProcedures} {metrics.totalProcedures === 1 ? 'Procedure' : 'Procedures'}
              </Typography>
            </Box>
            <Box className={classes.legend}>
              <Chip
                size="small"
                label={
                  <span>
                    <span className={`${classes.legendSwatch} ${classes.legendSwatchActual}`} /> Actual
                  </span>
                }
                variant="outlined"
              />
              <Chip
                size="small"
                label={
                  <span>
                    <span className={`${classes.legendSwatch} ${classes.legendSwatchScheduled}`} /> Scheduled
                  </span>
                }
                variant="outlined"
              />
              <Chip
                size="small"
                label={
                  <span>
                    <span className={classes.legendDash} /> Capacity
                  </span>
                }
                variant="outlined"
              />
              <Chip
                size="small"
                label="Symbols ℹ"
                variant="outlined"
                className={classes.symbolsChip}
                onClick={e => setSymbolsAnchorEl(e.currentTarget)}
              />
              <Popover
                open={Boolean(symbolsAnchorEl)}
                anchorEl={symbolsAnchorEl}
                onClose={() => setSymbolsAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box className={classes.symbolsPopover}>
                  <Typography className={classes.symbolsTitle}>Chart Symbols</Typography>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <rect
                          x="3"
                          y="3"
                          width="4"
                          height="4"
                          fill="#ffffff"
                          stroke="#666"
                          strokeWidth="0.5"
                          transform="rotate(45 5 5)"
                        />
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Patient Ready for Surgery</Typography>
                  </Box>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <rect x="3.5" y="3.5" width="3" height="3" rx="1.5" fill="#fbbf24" />
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Anesthesia Start</Typography>
                  </Box>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="12" height="10" viewBox="0 0 12 10">
                        <polygon points="3,2 9,2 6,7" fill="#3b82f6" />
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Physician In</Typography>
                  </Box>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="12" height="10" viewBox="0 0 12 10">
                        <polygon points="3,8 9,8 6,3" fill="#3b82f6" />
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Physician Out</Typography>
                  </Box>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="10" height="16" viewBox="0 0 10 16">
                        {[0, 3, 6, 9, 12].map(yy => (
                          <rect
                            key={yy}
                            x="4.5"
                            y={yy}
                            width="1"
                            height="2"
                            rx="0.5"
                            fill="rgba(255,255,255,0.65)"
                            stroke="#666"
                            strokeWidth="0.3"
                          />
                        ))}
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Procedure Start</Typography>
                  </Box>

                  <Box className={classes.symbolRow}>
                    <span className={classes.symbolIcon}>
                      <svg width="10" height="16" viewBox="0 0 10 16">
                        <line x1="5" y1="0" x2="5" y2="16" stroke="rgba(255,255,255,0.65)" strokeWidth="1" />
                        <line x1="5" y1="0" x2="5" y2="16" stroke="#666" strokeWidth="0.3" />
                      </svg>
                    </span>
                    <Typography className={classes.symbolLabel}>Procedure End</Typography>
                  </Box>
                </Box>
              </Popover>
            </Box>
            <Box className={classes.timeControls}>
              <FormControl size="small" className={classes.timeSelect} variant="outlined">
                <InputLabel>From</InputLabel>
                <Select value={chartStartHour} onChange={e => setChartStartHour(e.target.value as number)} label="From">
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      {String(i).padStart(2, '0')}:00
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="textSecondary">
                to
              </Typography>
              <FormControl size="small" className={classes.timeSelect} variant="outlined">
                <InputLabel>To</InputLabel>
                <Select
                  value={chartEndHour}
                  onChange={e => {
                    const newEndHour = e.target.value as number;
                    if (newEndHour > chartStartHour) {
                      setChartEndHour(newEndHour);
                    }
                  }}
                  label="To"
                >
                  {Array.from({ length: 25 }, (_, i) => i)
                    .filter(i => i > chartStartHour)
                    .map(i => (
                      <MenuItem key={i} value={i}>
                        {String(i).padStart(2, '0')}:00
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box className={classes.scrollContainer}>
            <GanttChart
              metrics={metrics}
              chartStartHour={chartStartHour}
              chartEndHour={chartEndHour}
              onProcedureHover={handleProcedureHover}
              physicianColorMap={physicianColorMap}
            />
          </Box>
        </Paper>
      ) : (
        <Paper>
          <Box p={4} textAlign="center">
            <Typography variant="h6" gutterBottom className={classes.emptyStateTitle}>
              {selectedDate ? 'No Procedures Found' : 'Select a Date'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedDate
                ? 'No procedures were scheduled for the selected date.'
                : 'Please select a date using the filter above to view the OR schedule.'}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Tooltip */}
      {hoveredProcedure && (
        <ProcedureTooltip procedure={hoveredProcedure} tooltipRef={tooltipRef} physicianColorMap={physicianColorMap} />
      )}
    </Box>
  );
};

export default ORScheduleAnalytics;
